import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, InputNumber, Switch, message, Space, Tag, Popconfirm, Alert, Card, Grid, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons';
import cdnService from '../../services/cdnService';

import { adminPath } from '../../../shared/config/paths';

const { useBreakpoint } = Grid;

/**
 * Admin per-domain Worker binding management.
 * Mirrors the user DomainWorker page, adapted for the admin domain-detail
 * context (uses domainData from the outlet; no subscription quota checks).
 */
export default function Worker() {
  const { domainData } = useOutletContext();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const domainName = domainData?.domain;
  const [domainWorkers, setDomainWorkers] = useState([]);
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBinding, setEditingBinding] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (domainName) {
      fetchDomainWorkers();
      fetchAvailableWorkers();
    }
  }, [domainName]);

  const fetchDomainWorkers = async () => {
    setLoading(true);
    try {
      const res = await cdnService.getDomainWorkers(domainName);
      if (res.success) {
        setDomainWorkers(res.data || []);
      }
    } catch (error) {
      message.error('加载失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableWorkers = async () => {
    try {
      const res = await cdnService.getAvailableWorkers(domainName);
      if (res.success) {
        setAvailableWorkers(res.data || []);
      }
    } catch (error) {
      message.error('加载可用Workers失败：' + error.message);
    }
  };

  const handleCreate = () => {
    setEditingBinding(null);
    form.resetFields();
    form.setFieldsValue({
      route_pattern: '/*',
      priority: 0,
      phase: 'request',
      enabled: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (binding) => {
    setEditingBinding(binding);
    form.setFieldsValue({
      worker_id: binding.worker_id,
      route_pattern: binding.route_pattern,
      priority: binding.priority,
      phase: binding.phase || 'request',
      enabled: binding.enabled,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await cdnService.deleteDomainWorker(domainName, id);
      if (res.success) {
        message.success('删除成功');
        fetchDomainWorkers();
      } else {
        message.error(res.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败：' + error.message);
    }
  };

  const handleToggle = async (binding) => {
    try {
      const res = await cdnService.toggleDomainWorker(domainName, binding.id, !binding.enabled);
      if (res.success) {
        message.success(binding.enabled ? '已禁用' : '已启用');
        fetchDomainWorkers();
      } else {
        message.error(res.error || '操作失败');
      }
    } catch (error) {
      message.error('操作失败：' + error.message);
    }
  };

  const handleViewWorker = (workerId) => {
    navigate(adminPath(`/cdn/workers/${workerId}`));
  };

  const handleManageWorkers = () => {
    navigate(adminPath('/cdn/workers'));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        worker_id: values.worker_id,
        route_pattern: values.route_pattern,
        priority: values.priority ?? 0,
        phase: values.phase || 'request',
        enabled: values.enabled ?? true,
      };

      if (editingBinding) {
        data.id = editingBinding.id;
      }

      const res = await cdnService.saveDomainWorker(domainName, data);

      if (res.success) {
        message.success(editingBinding ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchDomainWorkers();
      } else {
        message.error(res.error || '保存失败');
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('请检查表单输入');
      } else {
        message.error('保存失败：' + error.message);
      }
    }
  };

  const columns = [
    {
      title: 'Worker',
      dataIndex: 'worker_name',
      width: 200,
      render: (name, record) => (
        <div>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => handleViewWorker(record.worker_id)}
          >
            {name}
          </Button>
          <div style={{ fontSize: 12, color: '#999' }}>v{record.worker_version}</div>
        </div>
      ),
    },
    {
      title: '路由模式',
      dataIndex: 'route_pattern',
      width: 200,
      render: (pattern) => <code>{pattern}</code>,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      width: 100,
      sorter: (a, b) => b.priority - a.priority,
    },
    {
      title: '阶段',
      dataIndex: 'phase',
      width: 90,
      render: (phase) => (phase === 'response' ? <Tag color="purple">响应</Tag> : <Tag>请求</Tag>),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      width: 100,
      render: (enabled) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? '已启用' : '已禁用'}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleToggle(record)}
          >
            {record.enabled ? '禁用' : '启用'}
          </Button>
          <Popconfirm
            title="确定要删除这个Worker绑定吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderMobileCard = (binding) => (
    <Card
      key={binding.id}
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', fontWeight: 500 }}
            onClick={() => handleViewWorker(binding.worker_id)}
          >
            {binding.worker_name}
          </Button>
          <Tag color={binding.enabled ? 'green' : 'default'} style={{ marginLeft: 8, flexShrink: 0 }}>
            {binding.enabled ? '启用' : '禁用'}
          </Tag>
        </div>
      }
    >
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        <div style={{ color: '#666', marginBottom: 4 }}>路由模式:</div>
        <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
          {binding.route_pattern}
        </code>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Tag color="blue">v{binding.worker_version}</Tag>
        <Tag>优先级: {binding.priority}</Tag>
      </div>
      <Space wrap size="small">
        <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewWorker(binding.worker_id)}>
          查看
        </Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(binding)}>
          编辑
        </Button>
        <Button size="small" onClick={() => handleToggle(binding)}>
          {binding.enabled ? '禁用' : '启用'}
        </Button>
        <Popconfirm
          title="确定要删除这个Worker绑定吗？"
          onConfirm={() => handleDelete(binding.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button size="small" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div className="domain-worker-page" style={{ padding: isMobile ? '12px 0' : 0 }}>
      <Alert
        message="Worker路由绑定"
        description={isMobile
          ? `配置 ${domainName} 的Worker规则`
          : `为域名 ${domainName} 配置Worker执行规则。Worker将按优先级从高到低匹配路由模式。`
        }
        type="info"
        showIcon
        style={{ marginBottom: isMobile ? 12 : 16 }}
      />

      <div style={{
        marginBottom: isMobile ? 12 : 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <h3 style={{ margin: 0, fontSize: isMobile ? 16 : 18 }}>Worker绑定列表</h3>
        <Space size={isMobile ? 'small' : 'middle'} wrap>
          <Button
            icon={<SettingOutlined />}
            onClick={handleManageWorkers}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '管理' : '管理Workers'}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '添加' : '添加绑定'}
          </Button>
        </Space>
      </div>

      {isMobile ? (
        <div>
          {domainWorkers.map(binding => renderMobileCard(binding))}
          {domainWorkers.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              暂无 Worker 绑定
            </div>
          )}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={domainWorkers}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description="暂无 Worker 绑定，点击右上角添加绑定" /> }}
        />
      )}

      <Modal
        title={editingBinding ? '编辑Worker绑定' : '添加Worker绑定'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 20 } : undefined}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="选择Worker"
            name="worker_id"
            rules={[{ required: true, message: '请选择Worker' }]}
          >
            <Select
              placeholder="选择一个Worker"
              showSearch
              optionFilterProp="children"
              disabled={!!editingBinding}
            >
              {availableWorkers.map(worker => (
                <Select.Option key={worker.id} value={worker.id}>
                  {worker.name} (v{worker.version})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="路由模式"
            name="route_pattern"
            rules={[{ required: true, message: '请输入路由模式' }]}
            tooltip="* 匹配任意字符(含 /)；以 / 开头只匹配路径，也可带主机名：*.example.com/api/*"
          >
            <Input placeholder="例如: /api/*、*.example.com/*、/static/*.json" />
          </Form.Item>

          <Form.Item
            label="优先级"
            name="priority"
            tooltip="每个请求每个阶段只执行命中的最高优先级 Worker；同优先级时后创建的生效"
          >
            <InputNumber min={0} max={999} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="执行阶段" name="phase">
            <Select options={[{ value: 'request', label: '请求阶段' }, { value: 'response', label: '响应阶段' }]} />
          </Form.Item>

          <Form.Item label="启用" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
            <strong>路由模式示例：</strong><br />
            • <code>/api/*</code> - 匹配 /api/ 下所有路径(含 /api/users/123)<br />
            • <code>/*.json</code> - 匹配任意路径下的 .json 文件<br />
            • <code>api.example.com/*</code> - 只匹配该主机名<br />
            • <code>/admin/?/x</code> - ? 匹配单个字符
          </p>
        </div>
      </Modal>
    </div>
  );
}
